/**
 * 63.6 (the owner, Oct 10; lane F): foliage is concealment, not cover — but a round crossing it is slowed and nudged.
 * The crowns as the stands draw them (constants/treeFoliage.ts, measured: blobs as spheres, tiers as cones) are filed
 * per planet on the cells, apart from the hit shapes (so the cover tests' candidates are untouched, and nothing is built
 * while the switch is off); `foliageNear` and `foliageCrossed` give each tree a segment passes through and the depth of crown it crosses
 * (the union of its pieces' spans, so overlapping lumps are not counted twice). core/bullets.ts turns that into speed,
 * damage and a small turn (FOLIAGE_BULLETS). Arithmetic and `Math.sqrt` only; deterministic.
 */
import type { FloraState, GameState, PlanetState, Stance, Vec3 } from '../types';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { getSurfaceRadius, lastSurfaceTriangle } from './surface';
import { buildCellIndex, cellsPerEdge, queryCellIndexInto, cellsOfItems, fileCellIndex, kthOfSort, allFinite, cellOf, type CellIndex } from './cells';
import { floraSplice } from './floraSplice';
import { simWork } from './workCounters';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { seatRadius, fineEditsOf, sameEdits, editsChanged, nearEdits, type FineEdits } from './propSeat';
import { treeFrame, treeVariant, treeYaw } from './treeDraws';
import { TREE_FOLIAGE } from '../constants/treeFoliage';
import { treeForm, treeFormShape, formsKey } from './debris';
import { FOLIAGE_DENSITY, FOLIAGE_SIGHT, NORMAL_TREE_PERSONS, STANCE_SIZE, TREE_SINK_FRACTION } from '../constants/action';
import { TREE_WOOD } from '../constants/treeWood';
import { drawnBlobs, drawnPieces } from './treeShape';
import { reliefFacesAround, sameReliefAround } from './materialRelief';

/** A cone of crown along its own axis: from `base` up `axis` (unit) for `len`, its radius `r0` at the base, `r1` at the end. */
interface Cone { base: Vec3; axis: Vec3; len: number; r0: number; r1: number }
export interface FoliageTree {
  id: number;
  density: number;
  dir: Vec3;
  /** a sphere round the whole crown (a miss costs one test) */
  c: Vec3; r: number;
  balls: Array<{ c: Vec3; r: number }>;
  cones: Cone[];
}
export interface PlanetFoliage {
  list: FoliageTree[];
  /** the ordinary crowns on the cells (`filed[k]` = the list index of the index's item k) */
  index: CellIndex;
  filed: Int32Array;
  /** the widest an ordinary crown reaches from its tree's foot axis, and the tallest any stands (world units) */
  maxReach: number;
  maxTop: number;
  /** the few widest crowns (over 1.5 × the 90th percentile's reach), each by its bounding sphere — so they don't widen every query */
  large: number[];
}

/** 63.6's work, counted (the budget test): queries, candidate trees, crown pieces tested. */
export const foliageWork = { queries: 0, trees: 0, pieces: 0 };
export function resetFoliageWork(): void { foliageWork.queries = 0; foliageWork.trees = 0; foliageWork.pieces = 0; }

/**
 * The crowns depend on the trees, the planet's ground and the scale alone — keyed on those (not on the walk obstacles,
 * whose memo also turns over with every structure change: a crate's stock, a gun's health), so a battle rebuilds them
 * only when a tree falls or a crater moves the ground.
 */
let memo = new WeakMap<PlanetState, { flora: readonly unknown[]; config: unknown; forms: string | null; planetIndex: number; value: PlanetFoliage }>();
const EMPTY: PlanetFoliage = { list: [], index: buildCellIndex(0, () => ({ x: 0, y: 0, z: 1 }), 8), filed: new Int32Array(0), maxReach: 0, maxTop: 0, large: [] };

/** The planet's crowns (memoised on the trees, the planet and the config: a felled tree's crown leaves with the new flora array). */
export function planetFoliage(state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets'> & Partial<Pick<GameState, 'config' | 'floraHealth'>>, planetIndex: number): PlanetFoliage {
  const planet = state.planets[planetIndex];
  if (!planet) return EMPTY;
  const m = memo.get(planet);
  const forms = formsKey(state);   // partial destruction step 3: a tree's crown thins with its form (null with the switch off)
  if (m && m.flora === state.flora && m.config === state.config && m.forms === forms && m.planetIndex === planetIndex) return m.value;
  // E5.4's tail patch: the same planet, config and forms, the flora changed by forest cells joining and leaving alone
  if (m && m.config === state.config && m.forms === forms && m.planetIndex === planetIndex && state.flora) {
    const rec = recs.get(planetIndex), sp = rec && rec.value === m.value && rec.flora === m.flora ? floraSplice(m.flora as readonly FloraState[], state.flora) : null;
    if (sp && rec) {
      const value = joinFoliage(state, planetIndex, planet, rec, sp.removed, sp.addStart);
      if (value) { memo.set(planet, { flora: state.flora, config: state.config, forms, planetIndex, value }); return value; }
      simWork.tableJoinFallbacks++;
    }
  }
  // E5.4, craters and fellings in the wood: the same trees in the same places (a tree felled is an entry replaced in place),
  // the planet's ground perhaps moved — the crowns re-made only for the trees replaced and those over moved faces, the
  // rest as they were
  const rec = recs.get(planetIndex);
  if (rec?.planet && rec.tri && rec.config === state.config && rec.forms === forms && state.flora && rec.flora.length === state.flora.length
    && (rec.planet !== planet || rec.flora !== state.flora) && rec.planet.basePositions === planet.basePositions && rec.planet.radius === planet.radius
    && rec.planet.position.x === planet.position.x && rec.planet.position.y === planet.position.y && rec.planet.position.z === planet.position.z) {
    const value = placeFoliage(state, planetIndex, planet, rec);
    if (value) { memo.set(planet, { flora: state.flora, config: state.config, forms, planetIndex, value }); return value; }
  }
  const value = buildFoliage(state, planetIndex);
  memo.set(planet, { flora: state.flora, config: state.config, forms, planetIndex, value });
  return value;
}

/**
 * E5.4, craters and fellings in the wood: the last crowns patched in place — each tree replaced (felled: its crown gone)
 * and each crown over a face the crater moved (or whose face is not known) made again as the full build makes it, every
 * other crown kept; the tiers, the filing and the index kept where nothing they read moved, else read as the full build
 * reads them (the percentiles by selection). Null (a full build) for an entry that is another tree, a crater moving more
 * than an eighth of the ground, or a reach that is not finite.
 */
function placeFoliage(state: Pick<GameState, 'flora' | 'planets'> & Partial<Pick<GameState, 'config' | 'floraHealth'>>, planetIndex: number, planet: PlanetState, rec: FoliageRec): PlanetFoliage | null {
  const flora = state.flora!, oldList = rec.value.list, tri = rec.tri!;
  for (let j = 0; j < flora.length; j++) if (flora[j] !== rec.flora[j] && flora[j].id !== rec.flora[j].id) return null;
  // the faces the crater moved (the heightmap holds three heights a face)
  let moved: Set<number> | null = null;
  if (planet.heightmap !== rec.planet!.heightmap) {
    const h = planet.heightmap, before = rec.planet!.heightmap;
    if (h.length !== before.length) return null;
    moved = new Set();
    let changed = 0;
    for (let v = 0; v < h.length; v++) if (h[v] !== before[v]) { if (++changed > h.length / 8) return null; moved.add((v / 3) | 0); }
    // 71.2b: the relief by material reads the faces round a face's corners — those faces' trees stand on moved ground too
    if (state.config && personSetOf({ config: state.config }).materialRelief) moved = reliefFacesAround(planet, moved);
  }
  const person = PERSON_BODY_HEIGHT * (state.config ? personSetOf({ config: state.config }).k : 1);
  const list: FoliageTree[] = [], reachesL: number[] = [], topsL: number[] = [], trisL: number[] = [], cellsL: number[] = [];
  const slotOf = new Int32Array(flora.length).fill(-1);
  const keepCells = !!rec.cells;
  let tiersStand = true;
  for (let j = 0; j < flora.length; j++) {
    const sl = rec.slotOf[j];
    const redo = flora[j] !== rec.flora[j] || (sl >= 0 && moved !== null && (tri[sl] < 0 || moved.has(tri[sl])));
    if (!redo) {
      if (sl < 0) continue;
      slotOf[j] = list.length;
      list.push(oldList[sl]); reachesL.push(rec.reaches[sl]); topsL.push(rec.tops[sl]); trisL.push(tri[sl]);
      if (keepCells) cellsL.push(rec.cells![sl]);
      continue;
    }
    const c = crownOf(state, planet, planetIndex, person, flora[j]);
    if (!c) { if (sl >= 0) tiersStand = false; continue; }
    if (sl < 0 || !Object.is(c.reach, rec.reaches[sl]) || !Object.is(c.top, rec.tops[sl]) || c.tree.dir !== oldList[sl].dir && !(c.tree.dir.x === oldList[sl].dir.x && c.tree.dir.y === oldList[sl].dir.y && c.tree.dir.z === oldList[sl].dir.z)) tiersStand = false;
    slotOf[j] = list.length;
    list.push(c.tree); reachesL.push(c.reach); topsL.push(c.top); trisL.push(c.tri);
    if (keepCells) cellsL.push(cellOf(c.tree.dir.x, c.tree.dir.y, c.tree.dir.z, rec.n));
  }
  if (list.length !== oldList.length) tiersStand = false;
  let value: PlanetFoliage, cells: Uint32Array | undefined, n = rec.n;
  const reaches = Float64Array.from(reachesL), tops = Float64Array.from(topsL);
  if (tiersStand) {
    // the same crowns' places, reaches and tops: the tiers, the filing and the index as they were
    value = { ...rec.value, list };
    cells = keepCells ? Uint32Array.from(cellsL) : undefined;
  } else {
    if (!allFinite(reaches)) return null;
    const sel = Float64Array.from(reaches);
    const p90 = sel.length ? kthOfSort(sel, Math.floor((sel.length - 1) * 0.9)) : 0, median = sel.length ? kthOfSort(sel, sel.length >> 1) : 0;
    const filedIdx: number[] = [], large: number[] = [];
    let maxReach = 0, maxTop = 0;
    reachesL.forEach((r, i) => { if (r > 1.5 * p90) large.push(i); else { filedIdx.push(i); maxReach = Math.max(maxReach, r); } });
    for (const t of topsL) maxTop = Math.max(maxTop, t);
    n = cellsPerEdge(planet.radius, filedIdx.length ? 2 * median : planet.radius);
    cells = keepCells && rec.n === n ? Uint32Array.from(cellsL) : cellsOfItems(list.length, i => list[i].dir, n);
    value = { list, index: fileCellIndex(Uint32Array.from(filedIdx, i => cells![i]), n), filed: Int32Array.from(filedIdx), maxReach, maxTop, large };
  }
  lastFoliage.set(planetIndex, value);
  recs.set(planetIndex, { value, flora, slotOf, reaches, tops, cells, n, planet, config: state.config, forms: formsKey(state), tri: Int32Array.from(trisL) });
  simWork.tableCraterPatches++;
  return value;
}

function buildFoliage(state: Pick<GameState, 'flora' | 'planets'> & Partial<Pick<GameState, 'config' | 'floraHealth'>>, planetIndex: number): PlanetFoliage {
  const planet = state.planets[planetIndex];
  const person = PERSON_BODY_HEIGHT * (state.config ? personSetOf({ config: state.config }).k : 1);
  const list: FoliageTree[] = [];
  const reaches: number[] = [], tops: number[] = [], tris: number[] = [];
  const flora = state.flora ?? [];
  const slotOf = new Int32Array(flora.length).fill(-1);
  let maxTop = 0;
  for (let j = 0; j < flora.length; j++) {
    const c = crownOf(state, planet, planetIndex, person, flora[j]);
    if (!c) continue;
    slotOf[j] = list.length;
    list.push(c.tree); reaches.push(c.reach); tops.push(c.top); tris.push(c.tri);
    maxTop = Math.max(maxTop, c.top);
  }
  // two tiers, as the hit shapes have: the ordinary crowns on cells sized from a typical one, the few widest apart
  const sorted = Float64Array.from(reaches).sort();   // numeric ascending, as the comparator sort was
  const p90 = sorted.length ? sorted[Math.floor((sorted.length - 1) * 0.9)] : 0, median = sorted.length ? sorted[sorted.length >> 1] : 0;
  const filedIdx: number[] = [], large: number[] = [];
  let maxReach = 0;
  reaches.forEach((r, i) => { if (r > 1.5 * p90) large.push(i); else { filedIdx.push(i); maxReach = Math.max(maxReach, r); } });
  const typical = filedIdx.length ? 2 * median : planet.radius;
  // the last filing's index where the same directions are filed again at the same cells per edge (an index is those alone)
  const n = cellsPerEdge(planet.radius, typical), last = lastFoliage.get(planetIndex);
  const same = (a: Vec3, b: Vec3) => a === b || (a.x === b.x && a.y === b.y && a.z === b.z);
  const refile = last && last.index.n === n && last.filed.length === filedIdx.length && filedIdx.every((i, k) => same(list[i].dir, last.list[last.filed[k]].dir));
  const cells = refile ? undefined : cellsOfItems(list.length, i => list[i].dir, n);
  const value: PlanetFoliage = {
    list, index: refile ? last!.index : fileCellIndex(Uint32Array.from(filedIdx, i => cells![i]), n), filed: Int32Array.from(filedIdx),
    maxReach, maxTop, large,
  };
  lastFoliage.set(planetIndex, value);
  recs.set(planetIndex, { value, flora, slotOf, reaches: Float64Array.from(reaches), tops: Float64Array.from(tops), cells, n, planet, config: state.config, forms: formsKey(state), tri: Int32Array.from(tris) });
  return value;
}

/**
 * E5.4's tail patch (ARCHITECTURE §5.17): the last crowns patched for forest cells joining and leaving — the leaving trees'
 * crowns out, the joining ones' made (as the full build makes them) at the end (the forest's block is the flora's tail);
 * the 90th percentile and the median found as the full build's sort finds them, the tiers by its own comparison, the
 * index filed from the kept cells. Null (a full build) when a reach is not finite.
 */
function joinFoliage(state: Pick<GameState, 'flora' | 'planets'> & Partial<Pick<GameState, 'config' | 'floraHealth'>>, planetIndex: number, planet: PlanetState, rec: FoliageRec, removed: readonly number[], addStart: number): PlanetFoliage | null {
  const flora = state.flora!, oldList = rec.value.list;
  const person = PERSON_BODY_HEIGHT * (state.config ? personSetOf({ config: state.config }).k : 1);
  const list: FoliageTree[] = [], reachesL: number[] = [], topsL: number[] = [], cellsL: number[] = [], trisL: number[] = [];
  const slotOf = new Int32Array(flora.length).fill(-1);
  const keepCells = !!rec.cells;
  let j = 0;
  for (let i = 0, r = 0; i < rec.flora.length; i++) {
    if (r < removed.length && removed[r] === i) { r++; continue; }   // (`removed` is ascending)
    const sl = rec.slotOf[i];
    if (sl >= 0) { slotOf[j] = list.length; list.push(oldList[sl]); reachesL.push(rec.reaches[sl]); topsL.push(rec.tops[sl]); trisL.push(rec.tri ? rec.tri[sl] : -1); if (keepCells) cellsL.push(rec.cells![sl]); }
    j++;
  }
  for (let t = addStart; t < flora.length; t++) {
    const c = crownOf(state, planet, planetIndex, person, flora[t]);
    if (!c) continue;
    slotOf[t] = list.length;
    list.push(c.tree); reachesL.push(c.reach); topsL.push(c.top); trisL.push(c.tri);
    if (keepCells) cellsL.push(cellOf(c.tree.dir.x, c.tree.dir.y, c.tree.dir.z, rec.n));
  }
  const reaches = Float64Array.from(reachesL), tops = Float64Array.from(topsL);
  if (!allFinite(reaches)) return null;
  const sel = Float64Array.from(reaches);
  const p90 = sel.length ? kthOfSort(sel, Math.floor((sel.length - 1) * 0.9)) : 0, median = sel.length ? kthOfSort(sel, sel.length >> 1) : 0;
  const filedIdx: number[] = [], large: number[] = [];
  let maxReach = 0, maxTop = 0;
  reachesL.forEach((r, i) => { if (r > 1.5 * p90) large.push(i); else { filedIdx.push(i); maxReach = Math.max(maxReach, r); } });
  for (const t of topsL) maxTop = Math.max(maxTop, t);
  const typical = filedIdx.length ? 2 * median : planet.radius;
  const n = cellsPerEdge(planet.radius, typical);
  const cells = keepCells && rec.n === n ? Uint32Array.from(cellsL) : cellsOfItems(list.length, i => list[i].dir, n);
  const value: PlanetFoliage = { list, index: fileCellIndex(Uint32Array.from(filedIdx, i => cells[i]), n), filed: Int32Array.from(filedIdx), maxReach, maxTop, large };
  lastFoliage.set(planetIndex, value);
  recs.set(planetIndex, { value, flora, slotOf, reaches, tops, cells, n, planet, config: state.config, forms: formsKey(state), tri: Int32Array.from(trisL) });
  simWork.tableJoinPatches++;
  return value;
}

/**
 * A tree's crown on its planet (null: none — another planet's, felled, not a normal tree, or no foliage), kept while it,
 * its form, its ground and the config are the same: the full build's and the tail patch's one way to make it.
 */
function crownOf(state: Pick<GameState, 'flora' | 'planets'> & Partial<Pick<GameState, 'config' | 'floraHealth'>>, planet: PlanetState, planetIndex: number, person: number, f: FloraState): { tree: FoliageTree; reach: number; top: number; tri: number } | null {
  if (f.planetIndex !== planetIndex || !f.alive || f.size !== 'normal') return null;
  // partial destruction step 3: a tree's crown is kept while it, its form, its ground and the config are the same — a
  // felling or a form step rebuilds that tree's alone, not the planet's 2,000
  const form = treeForm(state, f);
  // (67.45: across a crater too — a crown reads the ground only at its tree's foot, which reads the three heights of the
  // face under it (which face never changes: heights move each vertex along its own radius); the same three heights, the
  // same crown. A tree with no crown reads no ground)
  const kept = crownKept.get(f);
  if (kept && kept.form === form && kept.config === state.config && kept.radius === planet.radius && kept.bp === planet.basePositions
    && kept.px === planet.position.x && kept.py === planet.position.y && kept.pz === planet.position.z
    && (sameEdits(kept.edits, planet) || !kept.tree || !state.config || !nearEdits(editsChanged(kept.edits, planet, personSetOf({ config: state.config }).k), planet, f.surfaceDir, 0))   // (71.2: its foot on the fine ground's edits too — kept unless one came or went within reach)
    && (kept.heightmap === planet.heightmap || !kept.tree || (state.config && personSetOf({ config: state.config }).materialRelief ? sameReliefAround(planet, kept.heightmap, planet.heightmap, kept.tri) : sameFace(kept.heightmap, planet.heightmap, kept.tri)))) {   // (71.2b: the relief by material reads the faces round its corners)
    kept.heightmap = planet.heightmap; kept.edits = fineEditsOf(planet);
    return kept.tree ? { tree: kept.tree, reach: kept.reach, top: kept.top, tri: kept.tri } : null;
  }
  const keep = (tree: FoliageTree | null, reach = 0, top = 0, tri = -1) => crownKept.set(f, { form, config: state.config, heightmap: planet.heightmap, edits: fineEditsOf(planet), bp: planet.basePositions, tri, radius: planet.radius, px: planet.position.x, py: planet.position.y, pz: planet.position.z, tree, reach, top });
  const density = FOLIAGE_DENSITY[f.floraType] ?? 0;
  const crown = treeFormShape(f, form) ?? TREE_FOLIAGE[f.floraType]?.variants[treeVariant(f.id, f.floraType)];   // its form's crown past whole
  if (!(density > 0) || !crown || (crown.blobs.length === 0 && crown.cones.length === 0)) { keep(null); return null; }
  const up = vecNormalize(f.surfaceDir);
  // (71.2: its foot on the fine ground at its trunk, as its wood and its drawing stand — core/propSeat.ts; the face under it read first)
  getSurfaceRadius(planet, up);
  const tri = lastSurfaceTriangle(), foot = vecAdd(planet.position, vecScale(up, seatRadius(planet, { dir: f.surfaceDir, half: 0 }, up, state.config ? personSetOf({ config: state.config }) : null)));
  const k = f.scale * NORMAL_TREE_PERSONS * person;
  const frame = treeFrame(up, treeYaw(f.id));
  // drawn sunk as its wood is (E5.2: TREE_SINK_FRACTION of the variant's height)
  const wood = TREE_WOOD[f.floraType]?.variants[treeVariant(f.id, f.floraType)], sink = TREE_SINK_FRACTION * (wood?.height ?? 0);
  const at = (x: number, y: number, z: number) => vecAdd(foot, vecScale(frame(x, y - sink, z), k));
  // (403: the crown as the tree draws it — its own height, width and lean)
  const balls = drawnBlobs(f, wood, crown.blobs).map(([x, y, z, r]) => ({ c: at(x, y, z), r: r * k }));
  const cones: Cone[] = [];
  for (const [ax, ay, az, ra, bx, by, bz, rb] of drawnPieces(f, wood, crown.cones)) {
    const a = at(ax, ay, az), b = at(bx, by, bz), ab = vecSub(b, a), len = vecLength(ab);
    if (len > 0) cones.push({ base: a, axis: vecScale(ab, 1 / len), len, r0: ra * k, r1: rb * k });
  }
  // the bound: round the mean of the pieces' centres
  const centres = [...balls.map(b => b.c), ...cones.map(c => vecAdd(c.base, vecScale(c.axis, c.len / 2)))];
  let c = { x: 0, y: 0, z: 0 };
  for (const p of centres) c = vecAdd(c, p);
  c = vecScale(c, 1 / centres.length);
  let r = 0;
  for (const b of balls) r = Math.max(r, vecLength(vecSub(b.c, c)) + b.r);
  for (const q of cones) r = Math.max(r, vecLength(vecSub(q.base, c)) + q.r0, vecLength(vecSub(vecAdd(q.base, vecScale(q.axis, q.len)), c)) + q.r1);
  const fromFoot = vecSub(c, foot), along = vecDot(fromFoot, up);
  const reach = vecLength(vecSub(fromFoot, vecScale(up, along))) + r, tree: FoliageTree = { id: f.id, density, dir: up, c, r, balls, cones };
  keep(tree, reach, along + r, tri);
  return { tree, reach, top: along + r, tri };
}

/** Partial destruction step 3: each tree's crown as last built, and each planet's last crowns (for their index). */
let crownKept = new WeakMap<FloraState, { form: number; config: unknown; heightmap: Float32Array; edits: FineEdits; bp: Float32Array; tri: number; radius: number; px: number; py: number; pz: number; tree: FoliageTree | null; reach: number; top: number }>();
/** 67.45: are the heights of face `tri` (its three vertex slots) the same in both heightmaps? (-1: no face was hit — not known) */
function sameFace(a: Float32Array, b: Float32Array, tri: number): boolean {
  if (tri < 0 || a.length !== b.length) return false;
  const i = tri * 3;
  return a[i] === b[i] && a[i + 1] === b[i + 1] && a[i + 2] === b[i + 2];
}
let lastFoliage = new Map<number, PlanetFoliage>();
/** E5.4's tail patch: each planet's last crowns with the flora they were made from, each flora entry's crown, each crown's reach and top, and its cell (when filed afresh). */
interface FoliageRec { value: PlanetFoliage; flora: readonly FloraState[]; slotOf: Int32Array; reaches: Float64Array; tops: Float64Array; cells: Uint32Array | undefined; n: number; /** E5.4, craters in the wood: what it stood on, each crown's face (-1: not known) */ planet?: PlanetState; config?: unknown; forms?: string | null; tri?: Int32Array }
let recs = new Map<number, FoliageRec>();
/** E5.4's tail patch, for the tests: `fn` with the memo empty (a fresh build on the kept records), then the memo and records put back. */
export function foliageBuildScope<T>(fn: () => T): T {
  const saved = { memo, lastFoliage: new Map(lastFoliage), recs: new Map(recs) };
  memo = new WeakMap();
  try { return fn(); } finally { memo = saved.memo; lastFoliage = saved.lastFoliage; recs = saved.recs; }
}

/** For the tests: run `fn` with no crown kept and no index reused (core/hits.ts `fromNothing` wraps it). */
export function foliageFromNothing<T>(fn: () => T): T {
  const saved = { crownKept, lastFoliage, memo, recs };
  crownKept = new WeakMap(); lastFoliage = new Map(); memo = new WeakMap(); recs = new Map();
  try { return fn(); } finally { ({ crownKept, lastFoliage, memo, recs } = saved); }
}

/** The span (in t of a + t·d, within [0, 1]) a segment spends inside a ball, or null. */
function ballSpan(a: Vec3, d: Vec3, c: Vec3, r: number): [number, number] | null {
  foliageWork.pieces++;
  const wx = a.x - c.x, wy = a.y - c.y, wz = a.z - c.z;
  const A = d.x * d.x + d.y * d.y + d.z * d.z, B = 2 * (wx * d.x + wy * d.y + wz * d.z), C = wx * wx + wy * wy + wz * wz - r * r;
  if (!(A > 0)) return C <= 0 ? [0, 1] : null;
  const disc = B * B - 4 * A * C;
  if (disc <= 0) return null;
  const s = Math.sqrt(disc);
  const t0 = Math.max(0, (-B - s) / (2 * A)), t1 = Math.min(1, (-B + s) / (2 * A));
  return t1 > t0 ? [t0, t1] : null;
}

/**
 * The spans (t within [0, 1]) a segment spends inside a cone of crown: between its two end planes, and within its
 * radius there — |w|² − h² ≤ (r0 + g·h)², a quadratic in t (two spans when the segment is steeper than the cone's side).
 */
function coneSpans(a: Vec3, d: Vec3, q: Cone, out: Array<[number, number]>): void {
  foliageWork.pieces++;
  const wx = a.x - q.base.x, wy = a.y - q.base.y, wz = a.z - q.base.z, ax = q.axis;
  const h0 = wx * ax.x + wy * ax.y + wz * ax.z, hd = d.x * ax.x + d.y * ax.y + d.z * ax.z;
  // between the end planes: 0 ≤ h0 + t·hd ≤ len
  let lo = 0, hi = 1;
  if (Math.abs(hd) < 1e-15) { if (h0 < 0 || h0 > q.len) return; }
  else {
    const ta = -h0 / hd, tb = (q.len - h0) / hd;
    lo = Math.max(lo, Math.min(ta, tb)); hi = Math.min(hi, Math.max(ta, tb));
    if (!(hi > lo)) return;
  }
  const g = (q.r1 - q.r0) / q.len, q0 = q.r0 + g * h0, q1 = g * hd;
  const A = d.x * d.x + d.y * d.y + d.z * d.z - hd * hd - q1 * q1, B = 2 * (wx * d.x + wy * d.y + wz * d.z - h0 * hd - q0 * q1), C = wx * wx + wy * wy + wz * wz - h0 * h0 - q0 * q0;
  const push = (x: number, y: number) => { const s0 = Math.max(lo, x), s1 = Math.min(hi, y); if (s1 > s0) out.push([s0, s1]); };
  if (Math.abs(A) < 1e-15) {
    if (Math.abs(B) < 1e-15) { if (C <= 0) push(lo, hi); return; }
    const t = -C / B;
    if (B > 0) push(-Infinity, t); else push(t, Infinity);
    return;
  }
  const disc = B * B - 4 * A * C;
  if (disc < 0) { if (A < 0) push(lo, hi); return; }
  const s = Math.sqrt(disc);
  const r0 = (-B - s) / (2 * A), r1 = (-B + s) / (2 * A);
  const t0 = Math.min(r0, r1), t1 = Math.max(r0, r1);
  if (A > 0) push(t0, t1);
  else { push(-Infinity, t0); push(t1, Infinity); }
}

const scratch: number[] = [];
const dirScratch = { x: 0, y: 0, z: 1 };
/** How far `c` is from the segment a→b (scalars). */
function pointSegment(c: Vec3, a: Vec3, b: Vec3): number {
  const abx = b.x - a.x, aby = b.y - a.y, abz = b.z - a.z, l2 = abx * abx + aby * aby + abz * abz;
  const t = l2 > 0 ? Math.max(0, Math.min(1, ((c.x - a.x) * abx + (c.y - a.y) * aby + (c.z - a.z) * abz) / l2)) : 0;
  const x = c.x - (a.x + abx * t), y = c.y - (a.y + aby * t), z = c.z - (a.z + abz * t);
  return Math.sqrt(x * x + y * y + z * z);
}
/**
 * The trees whose crowns a path through `points` (world, near planet `planetIndex`) could cross — one query round the
 * path's middle, widened by half its length and the widest crown (a bullet's tick is a few people long). Indices into
 * `planetFoliage(…).list`.
 */
export function foliageNear(points: readonly Vec3[], planetIndex: number, state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets'> & Partial<Pick<GameState, 'config'>>): number[] {
  const planet = state.planets[planetIndex];
  const pf = planetFoliage(state, planetIndex);
  if (!planet || pf.list.length === 0 || points.length === 0) return [];
  // (scalars throughout: this runs for every round every tick — 62.49's allocation lesson)
  let mx = 0, my = 0, mz = 0;
  for (const p of points) { mx += p.x; my += p.y; mz += p.z; }
  mx /= points.length; my /= points.length; mz /= points.length;
  let half = 0;
  for (const p of points) { const x = p.x - mx, y = p.y - my, z = p.z - mz; half = Math.max(half, Math.sqrt(x * x + y * y + z * z)); }
  const ox = mx - planet.position.x, oy = my - planet.position.y, oz = mz - planet.position.z, ol = Math.sqrt(ox * ox + oy * oy + oz * oz);
  const floor = ol - pf.maxTop - half;
  foliageWork.queries++;
  const a = points[0], b = points[points.length - 1];
  let sag = 0;
  for (const p of points) sag = Math.max(sag, pointSegment(p, a, b));
  // once a tick, the crowns whose bound comes within reach of the path's chord (widened by how far the path sags from
  // it) — so each substep tests only the few that matter
  const out: number[] = [];
  const keep = (i: number) => { foliageWork.trees++; const t = pf.list[i]; if (pointSegment(t.c, a, b) <= t.r + sag) out.push(i); };
  if (!(floor > 0) || pf.list.length < 16) { for (let i = 0; i < pf.list.length; i++) keep(i); return out; }
  dirScratch.x = ox / ol; dirScratch.y = oy / ol; dirScratch.z = oz / ol;
  for (const k of queryCellIndexInto(pf.index, dirScratch, (1.1 * (half + pf.maxReach)) / floor + 1e-9, scratch)) keep(pf.filed[k]);
  for (const i of pf.large) keep(i);
  return out;
}

const spans: Array<[number, number]> = [];
/** treeSpan's answer: the share (0…1) of the segment inside the crown, and where along it (0…1) it first enters. */
let spanDepth = 0, spanT = 0;
/**
 * Does the segment from `a` along `d` (L2 = |d|²) pass through tree `tr`'s crown? Its share inside — the union of its
 * pieces' spans — into spanDepth, its entry into spanT. foliageCrossed's own per-tree arithmetic, shared with the sight rule.
 */
function treeSpan(a: Vec3, d: Vec3, L2: number, tr: FoliageTree): boolean {
  // the bound first (scalars)
  const wx = a.x - tr.c.x, wy = a.y - tr.c.y, wz = a.z - tr.c.z;
  const tc = Math.max(0, Math.min(1, -(wx * d.x + wy * d.y + wz * d.z) / L2));
  const nx = wx + d.x * tc, ny = wy + d.y * tc, nz = wz + d.z * tc;
  if (nx * nx + ny * ny + nz * nz > tr.r * tr.r) return false;
  spans.length = 0;
  for (const ball of tr.balls) { const s = ballSpan(a, d, ball.c, ball.r); if (s) spans.push(s); }
  for (const q of tr.cones) coneSpans(a, d, q, spans);
  if (spans.length === 0) return false;
  spans.sort((x, y) => x[0] - y[0]);
  let depth = 0, s0 = spans[0][0], s1 = spans[0][1];
  for (let k = 1; k < spans.length; k++) {
    if (spans[k][0] > s1) { depth += s1 - s0; s0 = spans[k][0]; s1 = spans[k][1]; } else if (spans[k][1] > s1) s1 = spans[k][1];
  }
  depth += s1 - s0;
  spanDepth = depth; spanT = spans[0][0];
  return true;
}

/**
 * Of the trees `candidates` (foliageNear's), those the segment a→b passes through, in the order it enters them: each
 * one's id, its density and the depth of crown it crosses (world units), with the t it enters at.
 */
export function foliageCrossed(a: Vec3, b: Vec3, pf: PlanetFoliage, candidates: readonly number[]): Array<{ id: number; density: number; depth: number; t: number }> {
  if (candidates.length === 0) return [];
  const d = { x: b.x - a.x, y: b.y - a.y, z: b.z - a.z }, L2 = d.x * d.x + d.y * d.y + d.z * d.z, L = Math.sqrt(L2);
  if (!(L > 0)) return [];
  const out: Array<{ id: number; density: number; depth: number; t: number }> = [];
  for (const i of candidates) {
    const tr = pf.list[i];
    foliageWork.trees++;
    if (!treeSpan(a, d, L2, tr)) continue;
    out.push({ id: tr.id, density: tr.density, depth: spanDepth * L, t: spanT });
  }
  out.sort((x, y) => x.t - y.t || x.id - y.id);
  return out;
}

// ---------------------------------------------------------------------------
// 66.7: crowns hide from sight (§5.15; the match's `foliageSight` switch)
// ---------------------------------------------------------------------------

/**
 * The crown the segment a→b (world, near planet `planetIndex`) crosses — each crown's depth × its density (world units),
 * summed over the trees near it in their list order — stopping once it reaches `enough` (Infinity: all of it). With a stop,
 * the answer is whether the whole sum reaches it: the parts are never negative and are added in the same order.
 */
export function crownCrossed(a: Vec3, b: Vec3, planetIndex: number, state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets'> & Partial<Pick<GameState, 'config'>>, enough = Infinity): number {
  const pf = planetFoliage(state, planetIndex);
  if (pf.list.length === 0) return 0;
  const d = { x: b.x - a.x, y: b.y - a.y, z: b.z - a.z }, L2 = d.x * d.x + d.y * d.y + d.z * d.z, L = Math.sqrt(L2);
  if (!(L > 0)) return 0;
  const cands = foliageNear([a, b], planetIndex, state).sort((x, y) => x - y);
  let sum = 0;
  for (const i of cands) {
    const tr = pf.list[i];
    foliageWork.trees++;
    if (!treeSpan(a, d, L2, tr)) continue;
    sum += spanDepth * L * tr.density;
    if (sum >= enough) return sum;
  }
  return sum;
}

/** The crown (depth × density, world units) that hides a body at `stance`: FOLIAGE_SIGHT.hidePeople standing, less by its posed top lower down (STANCE_SIZE). */
export function crownToHide(stance: Stance | undefined, person: number): number {
  return FOLIAGE_SIGHT.hidePeople * person * (STANCE_SIZE[stance ?? 'stand'].top / STANCE_SIZE.stand.top);
}

/**
 * 66.7: with the `foliageSight` switch on, does foliage hide a body at `stance` (its point `target`) from an eye at `eye`?
 * When the crown the line crosses reaches `crownToHide(stance)` and the body is further than FOLIAGE_SIGHT.armPeople.
 * Off: never — and nothing is computed, so with the switch off nothing changes.
 */
export function crownHides(state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets' | 'config'>, planetIndex: number, eye: Vec3, target: Vec3, stance?: Stance): boolean {
  // (447: the rainforest hides by its trees' crowns alone — the drawn layers and their depth field are gone)
  if (!state.config.foliageSight) return false;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  if (vecLength(vecSub(target, eye)) <= FOLIAGE_SIGHT.armPeople * person) return false;
  const need = crownToHide(stance, person);
  return crownCrossed(eye, target, planetIndex, state, need) >= need;
}
